import { Button, HSpacer, MenuItem, Text } from "@/components/DesignSystem";
import { RetailerAvatar } from "@/components/DesignSystem/Avatar/RetailerAvatar";
import { useDefaultRetailerId } from '@/components/shared/DefaultRetailerSelector';
import { useAuthentication } from '@/contexts/dataSync/AuthenticationContext';
import { useGetRetailerList } from "@/hooks/useProductQuery";
import { RetailerApi } from "@/utilities/api/RetailerApi";
import { ApiRetailer } from "@api/interfaces";
import Check from "@mui/icons-material/Check";
import ArrowDropDown from "@mui/icons-material/ArrowDropDown";
import { ListItemIcon, ListItemText, Menu, Stack } from "@mui/material";
import React, { useCallback, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';

enum QueryParams {
  'retailerId' = 'retailerId',
}

const ERPRetailerSelector = ({
  header,
  logo,
  onSelect,
} : {
  header: string,
  logo?: 'growers' | 'manufacturer' | 'product' | 'store' | 'none'
  onSelect: (retailer?: ApiRetailer) => void,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedRetailer, setSelectedRetailer] = useState<null | ApiRetailer>();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { isInternal } = useAuthentication();
  const { defaultRetailerId } = useDefaultRetailerId();
  const { t } = useTranslation();

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleRetailerSelected = useCallback(async (selectedRetailerId?: string) => {
    setAnchorEl(null);

    if (!selectedRetailerId) {
      setSelectedRetailer(null);
      onSelect();
      return;
    }

    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set(QueryParams.retailerId, selectedRetailerId);
      return newParams;
    });

    const { data } = await RetailerApi.list(
      { id: [selectedRetailerId] },
    );
    setSelectedRetailer(data[0]);
    onSelect(data[0]);
  }, []);

  const { retailerList } = useGetRetailerList({
  }, {
    enabled: isInternal,
    onSuccess: (data) => {
      const isDefaultRetailerActive = data?.some((retailer) => retailer.id === defaultRetailerId);
      const selectedRetailerId = searchParams.get(QueryParams.retailerId)
        ?? (isDefaultRetailerActive ? defaultRetailerId : data?.[0]?.id);
      void handleRetailerSelected(selectedRetailerId ?? undefined);
    },
  });

  if (!isInternal) {
    return (
      <Stack alignItems="center" direction="row">
        <Text category="display-small">{header}</Text>
      </Stack>
    );
  }

  return (
    <Stack alignItems="center" direction="row">
      <Text category="display-small">{header}</Text>
        <HSpacer size='6' />
        <Button
          onClick={(e) => handleClick(e)}
          square={true}
          testID="retailer-selector-menu"
        >
          <Stack alignItems="center" direction="row">
            <Text category="label-medium">{t($ => $.common.list.selectRetailer)}</Text>
            <HSpacer size="3"/>
            <ArrowDropDown sx={{ height: '16px', width: '16px' }} />
          </Stack>
        </Button>
        <HSpacer size='5' />
        <Menu anchorEl={anchorEl} onClose={handleClose} open={open}>
          {retailerList?.map((retailer) => {
            return (
              <MenuItem
                key={retailer.id}
                onClick={() => handleRetailerSelected(retailer.id)}
                selected={selectedRetailer?.id === retailer.id}
                testID={`menu-item-${retailer.id}`}
                value={retailer.id}
              >
                <ListItemText>{retailer.name}</ListItemText>
                {selectedRetailer?.id === retailer.id && (
                  <ListItemIcon>
                    <Check color='primary' />
                  </ListItemIcon>
                )}
              </MenuItem>
            );
          })}
        </Menu>
        {(!!selectedRetailer || logo !== 'none') && (
          <RetailerAvatar retailer={selectedRetailer} size={24} />
        )}
        <HSpacer size="4" />
        <Text category="title-large">
          {selectedRetailer ? selectedRetailer.name : ''}
        </Text>
    </Stack>
  );
};

export default ERPRetailerSelector;
